import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { RiWhatsappLine as Whatsapp } from '@remixicon/react';

export interface HeroSlideItem {
    id: string;
    type: 'image' | 'video';
    src: string;
    videoSrc?: string;
    title: string;
    titleAccent: string;
    accentColor: string;
    description: string;
    ctaPrimaryText?: string;
    ctaSecondaryText?: string;
    secondaryUrl?: string;
}

interface AstonMartinHeroProps {
    slides: HeroSlideItem[];
    onCotizarClick?: () => void;
    advisorWhatsappUrl?: string;
}

export const AstonMartinHero: React.FC<AstonMartinHeroProps> = ({
    slides,
    onCotizarClick,
    advisorWhatsappUrl = 'https://wa.me/59176204601?text=Hola%20Co%20Marca,%20deseo%20m%C3%A1s%20informaci%C3%B3n',
}) => {
    const totalOriginalSlides = slides.length;

    // ── Build extended slides array for seamless infinite looping ──
    // [Clone of Last, Slide 0, Slide 1, Slide 2, Clone of First]
    const extendedSlides = useMemo(() => {
        if (totalOriginalSlides <= 1) return slides;
        const lastClone: HeroSlideItem = {
            ...slides[totalOriginalSlides - 1],
            id: `${slides[totalOriginalSlides - 1].id}-clone-start`,
        };
        const firstClone: HeroSlideItem = {
            ...slides[0],
            id: `${slides[0].id}-clone-end`,
        };
        return [lastClone, ...slides, firstClone];
    }, [slides, totalOriginalSlides]);

    const totalTrackItems = extendedSlides.length;

    // Track index starts at 1 (real first slide)
    const [trackIndex, setTrackIndex] = useState(1);
    const [withTransition, setWithTransition] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState<number | null>(null);
    const [dragOffset, setDragOffset] = useState(0);
    const [hasDragged, setHasDragged] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

    // Calculate logical 0-based index for dots and counter (0, 1, 2)
    const logicalIndex = useMemo(() => {
        if (totalOriginalSlides <= 1) return 0;
        if (trackIndex === 0) return totalOriginalSlides - 1;
        if (trackIndex === totalTrackItems - 1) return 0;
        return trackIndex - 1;
    }, [trackIndex, totalOriginalSlides, totalTrackItems]);

    // Navigation triggers (guarded by isTransitioning to avoid glitching)
    const nextSlide = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setWithTransition(true);
        setTrackIndex((prev) => prev + 1);
    }, [isTransitioning]);

    const prevSlide = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setWithTransition(true);
        setTrackIndex((prev) => prev - 1);
    }, [isTransitioning]);

    const goToSlide = useCallback((origIdx: number) => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setWithTransition(true);
        setTrackIndex(origIdx + 1);
    }, [isTransitioning]);

    // Re-enable CSS transitions after instant clone-to-original jump
    useEffect(() => {
        if (!withTransition) {
            const raf1 = requestAnimationFrame(() => {
                const raf2 = requestAnimationFrame(() => {
                    setWithTransition(true);
                    setIsTransitioning(false);
                });
                return () => cancelAnimationFrame(raf2);
            });
            return () => cancelAnimationFrame(raf1);
        }
    }, [withTransition]);

    // Handle seamless boundary wrap when transition finishes
    const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        // Ensure we only handle the track's own transform transition
        if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;

        setIsTransitioning(false);

        if (trackIndex === totalTrackItems - 1) {
            // Arrived at the end clone (Clone of First Slide) -> jump silently to real first slide (index 1)
            setWithTransition(false);
            setTrackIndex(1);
        } else if (trackIndex === 0) {
            // Arrived at the start clone (Clone of Last Slide) -> jump silently to real last slide
            setWithTransition(false);
            setTrackIndex(totalOriginalSlides);
        }
    };

    // Autoplay (9s) - smoothly progresses continuously
    useEffect(() => {
        if (isDragging || isHovered) {
            if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
            return;
        }

        autoplayTimerRef.current = setTimeout(() => {
            nextSlide();
        }, 10500);

        return () => {
            if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
        };
    }, [trackIndex, isDragging, isHovered, nextSlide]);

    // Keyboard navigation (ArrowLeft & ArrowRight)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

            if (e.key === 'ArrowRight') {
                e.preventDefault();
                nextSlide();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                prevSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [nextSlide, prevSlide]);

    // Manage video playback across visible/active slides
    useEffect(() => {
        videoRefs.current.forEach((video, idx) => {
            if (!video) return;
            // Play if this slide matches trackIndex or represents the active logical slide
            if (idx === trackIndex) {
                video.currentTime = 0;
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        });
    }, [trackIndex]);

    // Pointer / Touch / Drag Handlers
    const handlePointerDown = (e: React.PointerEvent) => {
        if ((e.target as HTMLElement).closest('button, a')) return;

        setIsDragging(true);
        setDragStartX(e.clientX);
        setDragOffset(0);
        setHasDragged(false);

        if (containerRef.current) {
            containerRef.current.setPointerCapture(e.pointerId);
        }
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging || dragStartX === null) return;

        const currentX = e.clientX;
        const diffX = currentX - dragStartX;

        if (Math.abs(diffX) > 6) {
            setHasDragged(true);
        }

        setDragOffset(diffX);
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        if (!isDragging) return;

        if (containerRef.current) {
            try {
                containerRef.current.releasePointerCapture(e.pointerId);
            } catch (_) {}
        }

        const threshold = 65; // Pixels required to trigger slide advance

        if (dragOffset < -threshold) {
            nextSlide();
        } else if (dragOffset > threshold) {
            prevSlide();
        }

        setIsDragging(false);
        setDragStartX(null);
        setDragOffset(0);
    };

    const handlePointerCancel = (e: React.PointerEvent) => {
        if (containerRef.current) {
            try {
                containerRef.current.releasePointerCapture(e.pointerId);
            } catch (_) {}
        }
        setIsDragging(false);
        setDragStartX(null);
        setDragOffset(0);
    };

    // Track positioning percentages
    const trackWidthPercent = totalTrackItems * 100;
    const baseTranslatePercent = -(trackIndex * (100 / totalTrackItems));

    return (
        <section
            ref={containerRef}
            className="aston-martin-hero"
            aria-label="Carrusel principal"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                position: 'relative',
                width: '100vw',
                height: '100vh',
                minHeight: '100vh',
                overflow: 'hidden',
                backgroundColor: '#0a0512',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'pan-y',
            }}
        >
            <style>{`
                /* ── ASTON MARTIN LUXURY SLOW & SMOOTH TRANSITIONS ── */
                .aston-martin-hero {
                    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                }

                /* Nav side arrows */
                .am-arrow-btn {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 40;
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    border: 1px solid rgba(255, 255, 255, 0.24);
                    background: rgba(15, 8, 26, 0.28);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
                    opacity: 0.85;
                }
                .am-arrow-btn:hover {
                    opacity: 1;
                    background: rgba(255, 255, 255, 0.26);
                    border-color: rgba(255, 255, 255, 0.65);
                    transform: translateY(-50%) scale(1.08);
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.38);
                }
                .am-arrow-btn:active {
                    transform: translateY(-50%) scale(0.95);
                }
                .am-arrow-prev { left: clamp(16px, 3.2vw, 44px); }
                .am-arrow-next { right: clamp(16px, 3.2vw, 44px); }

                /* Call to Action Buttons */
                .am-btn-primary {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    padding: 13px 28px;
                    border-radius: 9999px;
                    background: linear-gradient(135deg, #c1348b 0%, #61257B 100%);
                    color: #ffffff;
                    font-size: 0.82rem;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    box-shadow: 0 8px 24px rgba(193, 52, 139, 0.38);
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .am-btn-primary:hover {
                    transform: translateY(-3px) scale(1.02);
                    filter: brightness(1.1);
                    box-shadow: 0 16px 36px rgba(193, 52, 139, 0.55);
                }
                .am-btn-primary:active {
                    transform: translateY(-1px) scale(0.98);
                }

                .am-btn-secondary {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    padding: 12px 24px;
                    border-radius: 9999px;
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    color: #ffffff;
                    border: 1px solid rgba(255, 255, 255, 0.32);
                    font-size: 0.82rem;
                    font-weight: 600;
                    letter-spacing: 0.03em;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .am-btn-secondary:hover {
                    transform: translateY(-3px) scale(1.02);
                    background: rgba(255, 255, 255, 0.25);
                    border-color: rgba(255, 255, 255, 0.65);
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
                }
                .am-btn-secondary:active {
                    transform: translateY(-1px) scale(0.98);
                }

                /* Indicators & Progress lines */
                .am-indicator-line {
                    height: 3px;
                    border-radius: 3px;
                    cursor: pointer;
                    background: rgba(255, 255, 255, 0.3);
                    transition: all 0.45s cubic-bezier(0.22, 1, 0.36, 1);
                    position: relative;
                    overflow: hidden;
                }
                .am-indicator-line.active {
                    background: rgba(255, 255, 255, 0.55);
                    width: 48px !important;
                }
                .am-indicator-progress {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 0%;
                    background: #c1348b;
                }
                .am-indicator-line.active .am-indicator-progress {
                    width: 100%;
                    transition: width 10.5s linear;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .am-hero-content {
                        bottom: 36px !important;
                        left: 18px !important;
                        right: 18px !important;
                        max-width: 100% !important;
                    }
                    .am-hero-title {
                        font-size: clamp(1.8rem, 6.8vw, 2.3rem) !important;
                    }
                    .am-hero-ctas {
                        flex-direction: column !important;
                        align-items: stretch !important;
                        gap: 10px !important;
                    }
                    .am-arrow-btn {
                        display: none !important;
                    }
                }
            `}</style>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* ── TRACK PRINCIPAL: BUCLE INFINITO CONTINUO Y SUAVE ── */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <div
                onTransitionEnd={handleTransitionEnd}
                style={{
                    display: 'flex',
                    width: `${trackWidthPercent}%`,
                    height: '100%',
                    transform: `translateX(calc(${baseTranslatePercent}% + ${dragOffset}px))`,
                    // Curva bezier ultra-suave y duración majestuosa de 1900ms (cambio lento y elegante)
                    transition:
                        isDragging || !withTransition
                            ? 'none'
                            : 'transform 1900ms cubic-bezier(0.16, 1, 0.3, 1)',
                    willChange: 'transform',
                }}
            >
                {extendedSlides.map((slide, idx) => {
                    const isActive = idx === trackIndex;
                    // Parallax sutil desacoplado durante el arrastre físico
                    const decoupledDragOffset = isDragging ? dragOffset * 0.32 : 0;

                    return (
                        <div
                            key={slide.id}
                            style={{
                                position: 'relative',
                                width: `${100 / totalTrackItems}%`,
                                height: '100%',
                                flexShrink: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {/* ── Background Media: CLARO, NÍTIDO, LUZ NATURAL ── */}
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 1,
                                    overflow: 'hidden',
                                    pointerEvents: 'none',
                                }}
                            >
                                {slide.type === 'video' && slide.videoSrc ? (
                                    <video
                                        ref={(el) => {
                                            videoRefs.current[idx] = el;
                                        }}
                                        src={slide.videoSrc}
                                        poster={slide.src}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: `scale(${isActive ? 1.0 : 1.08}) translateX(${isDragging ? -dragOffset * 0.12 : 0}px)`,
                                            transition: isDragging || !withTransition ? 'none' : 'transform 2100ms cubic-bezier(0.16, 1, 0.3, 1)',
                                            willChange: 'transform',
                                            filter: 'brightness(0.97) contrast(1.03)',
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={slide.src}
                                        alt={`${slide.title} ${slide.titleAccent}`}
                                        draggable={false}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: `scale(${isActive ? 1.0 : 1.08}) translateX(${isDragging ? -dragOffset * 0.12 : 0}px)`,
                                            transition: isDragging || !withTransition ? 'none' : 'transform 2100ms cubic-bezier(0.16, 1, 0.3, 1)',
                                            willChange: 'transform',
                                            filter: 'brightness(0.98) contrast(1.02)',
                                        }}
                                    />
                                )}
                            </div>

                            {/* ── SCRIM DEGRADADO CINEMATOGRÁFICO LOCALIZADO (ESQUINA INFERIOR IZQUIERDA) ── */}
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 2,
                                    pointerEvents: 'none',
                                    background:
                                        'linear-gradient(to top, rgba(10, 5, 18, 0.88) 0%, rgba(10, 5, 18, 0.46) 28%, rgba(10, 5, 18, 0.06) 60%, transparent 100%)',
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 2,
                                    pointerEvents: 'none',
                                    background:
                                        'radial-gradient(ellipse at 12% 88%, rgba(13, 6, 24, 0.7) 0%, rgba(13, 6, 24, 0.28) 45%, transparent 75%)',
                                }}
                            />

                            {/* ══════════════════════════════════════════════════════════════ */}
                            {/* ── CONTENIDO DESACOPLADO CON DESACELERACIÓN SUAVE Y ELEGANTE ── */}
                            {/* ══════════════════════════════════════════════════════════════ */}
                            <div
                                className="am-hero-content"
                                style={{
                                    position: 'relative',
                                    zIndex: 10,
                                    paddingBottom: 'clamp(50px, 8.5vh, 92px)',
                                    paddingLeft: 'clamp(28px, 6vw, 88px)',
                                    paddingRight: 'clamp(24px, 4vw, 60px)',
                                    maxWidth: 'min(700px, 90vw)',
                                    transform: `translateX(${decoupledDragOffset}px)`,
                                    transition: isDragging ? 'none' : 'transform 1200ms cubic-bezier(0.16, 1, 0.3, 1)',
                                }}
                            >
                                {/* ── TÍTULO (PALETA DE MARCA, TIPOGRAFÍA ELEGANTE) ── */}
                                <h1
                                    className="am-hero-title"
                                    style={{
                                        fontFamily: "'Montserrat', 'DM Sans', sans-serif",
                                        fontSize: 'clamp(1.9rem, 3.4vw, 2.9rem)',
                                        fontWeight: 900,
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.025em',
                                        textTransform: 'uppercase',
                                        margin: '0 0 16px 0',
                                        color: '#ffffff',
                                        textShadow: '0 4px 18px rgba(0, 0, 0, 0.55)',
                                        opacity: isActive ? 1 : 0,
                                        transform: isActive
                                            ? 'translateY(0px)'
                                            : `translateY(18px) translateX(${idx > trackIndex ? '36px' : '-36px'})`,
                                        transition: isDragging || !withTransition
                                            ? 'none'
                                            : isActive
                                            ? 'opacity 1300ms cubic-bezier(0.16, 1, 0.3, 1) 320ms, transform 1300ms cubic-bezier(0.16, 1, 0.3, 1) 320ms'
                                            : 'opacity 650ms ease, transform 650ms ease',
                                    }}
                                >
                                    {slide.title}{' '}
                                    <span
                                        style={{
                                            color: slide.accentColor || '#c1348b',
                                            textShadow: '0 0 24px rgba(193, 52, 139, 0.45)',
                                        }}
                                    >
                                        {slide.titleAccent}
                                    </span>
                                </h1>

                                {/* ── SUBTÍTULO DESCRIPTIVO ── */}
                                <p
                                    style={{
                                        fontSize: 'clamp(0.92rem, 1.2vw, 1.08rem)',
                                        color: 'rgba(255, 255, 255, 0.88)',
                                        lineHeight: 1.6,
                                        maxWidth: '560px',
                                        margin: '0 0 28px 0',
                                        textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                                        opacity: isActive ? 1 : 0,
                                        transform: isActive
                                            ? 'translateY(0px)'
                                            : `translateY(18px) translateX(${idx > trackIndex ? '28px' : '-28px'})`,
                                        transition: isDragging || !withTransition
                                            ? 'none'
                                            : isActive
                                            ? 'opacity 1300ms cubic-bezier(0.16, 1, 0.3, 1) 480ms, transform 1300ms cubic-bezier(0.16, 1, 0.3, 1) 480ms'
                                            : 'opacity 650ms ease, transform 650ms ease',
                                    }}
                                >
                                    {slide.description}
                                </p>

                                {/* ── BOTONES DE LLAMADA A LA ACCIÓN ── */}
                                <div
                                    className="am-hero-ctas"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '14px',
                                        flexWrap: 'wrap',
                                        opacity: isActive ? 1 : 0,
                                        transform: isActive
                                            ? 'translateY(0px)'
                                            : `translateY(20px) translateX(${idx > trackIndex ? '22px' : '-22px'})`,
                                        transition: isDragging || !withTransition
                                            ? 'none'
                                            : isActive
                                            ? 'opacity 1300ms cubic-bezier(0.16, 1, 0.3, 1) 640ms, transform 1300ms cubic-bezier(0.16, 1, 0.3, 1) 640ms'
                                            : 'opacity 650ms ease, transform 650ms ease',
                                    }}
                                >
                                    {/* Botón Principal (Cotizar) */}
                                    <button
                                        type="button"
                                        className="am-btn-primary"
                                        onClick={(e) => {
                                            if (hasDragged) {
                                                e.preventDefault();
                                                return;
                                            }
                                            if (onCotizarClick) onCotizarClick();
                                        }}
                                    >
                                        <span>{slide.ctaPrimaryText || 'Cotizar algún servicio'}</span>
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </button>

                                    {/* Botón Secundario (Asesor WhatsApp) */}
                                    <a
                                        href={slide.secondaryUrl || advisorWhatsappUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="am-btn-secondary"
                                        onClick={(e) => {
                                            if (hasDragged) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <Whatsapp size={17} color="#25D366" />
                                        <span>{slide.ctaSecondaryText || 'Hablar con un asesor'}</span>

                                    </a>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* ── FLECHAS SUTILES ESTILO ASTON MARTIN ── */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <button
                type="button"
                className="am-arrow-btn am-arrow-prev"
                onClick={prevSlide}
                aria-label="Diapositiva anterior"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                </svg>
            </button>

            <button
                type="button"
                className="am-arrow-btn am-arrow-next"
                onClick={nextSlide}
                aria-label="Diapositiva siguiente"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                </svg>
            </button>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* ── PAGINACIÓN / INDICADORES MINIMALISTAS ── */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 'clamp(20px, 4vh, 36px)',
                    right: 'clamp(28px, 6vw, 88px)',
                    zIndex: 40,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                }}
            >
                {/* Indicador numérico sincronizado con el índice lógico */}
                <span
                    style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        color: 'rgba(255, 255, 255, 0.75)',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    0{logicalIndex + 1} / 0{totalOriginalSlides}
                </span>

                {/* Líneas horizontales de progreso */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {slides.map((_, idx) => (
                        <div
                            key={idx}
                            className={`am-indicator-line ${idx === logicalIndex ? 'active' : ''}`}
                            onClick={() => goToSlide(idx)}
                            style={{
                                width: idx === logicalIndex ? '44px' : '18px',
                            }}
                        >
                            {idx === logicalIndex && <div className="am-indicator-progress" />}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AstonMartinHero;
